import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, X, AlertTriangle, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
// Removed framer-motion as shadcn handles animations
// Removed Card as PopoverContent provides the container style

// Si la variable de entorno está vacía (Prod), usamos el origen actual (para que funcione el proxy /api)
// Si tiene valor (Dev), usamos ese valor (ej: http://localhost:8000)
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = '';

if (!ENV_BACKEND_URL || ENV_BACKEND_URL === '') {
  // Producción: Usar URL relativa
  BACKEND_URL = window.location.origin; 
} else {
  // Desarrollo: Usar valor explícito
  BACKEND_URL = ENV_BACKEND_URL;
}

// Limpiar si trae /api al final
if (BACKEND_URL.endsWith('/api')) {
  BACKEND_URL = BACKEND_URL.slice(0, -4);
}

const API = `${BACKEND_URL}/api`;

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const [stockResponse, debtResponse] = await Promise.all([
        axios.get(`${API}/notifications/low-stock`),
        axios.get(`${API}/notifications/high-debt-alerts`)
      ]);
      
      const combined = [...stockResponse.data, ...debtResponse.data];
      setNotifications(combined);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const criticalCount = notifications.filter(n => n.severity === 'high').length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative w-full justify-start px-2" // Adjusted for sidebar context
          data-testid="notifications-btn"
        >
          <Bell className="h-5 w-5 mr-3" /> {/* Adjusted icon size/margin for sidebar consistency */}
          <span>Notificaciones</span>
          {criticalCount > 0 && (
            <span className="ml-auto bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
              {criticalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-96 max-h-[500px] overflow-y-auto p-0" 
        align="start" 
        side="right" 
        sideOffset={10}
      >
        <div className="p-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Notificaciones</h3>
                <Button variant="ghost" size="icon h-6 w-6" onClick={() => setIsOpen(false)}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {loading ? (
                <p className="text-muted-foreground text-center py-8">Cargando...</p>
            ) : notifications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay notificaciones</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {notifications.map((notification) => (
                    <div
                        key={notification.id}
                        className={`p-3 rounded-lg border ${
                        notification.severity === 'high'
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                            : 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                        }`}
                    >
                        <div className="flex items-start space-x-3">
                        <AlertTriangle
                            className={`h-5 w-5 mt-0.5 ${
                            notification.severity === 'high'
                                ? 'text-orange-600'
                                : 'text-yellow-600'
                            }`}
                        />
                        <div className="flex-1">
                            <p className="font-medium text-sm">{notification.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                            {notification.message}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                            <span className="text-xs font-mono">
                                Stock: {notification.current_stock}/{notification.min_stock}
                            </span>
                            <span
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                notification.severity === 'high'
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                                }`}
                            >
                                {notification.severity === 'high' ? 'Crítico' : 'Medio'}
                            </span>
                            </div>
                        </div>
                        </div>
                    </div>
                    ))}
                </div>
            )}
        </div>
      </PopoverContent>
    </Popover>
  );
}